import { ApolloClient, gql, useQuery } from '@apollo/client'
import compact from 'lodash/compact'
import {
  GetSweepstakeQuery,
  GetSweepstakeQueryVariables,
  GetSweepstakesQuery,
  GetSweepstakesQueryVariables,
  SweepstakeObject,
  SweepstakeStatus,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'

export const GET_SWEEPSTAKE = gql`
  query getSweepstake($slug: String!) {
    getSweepstake(input: { projectSlug: $slug }) {
      sweepstake {
        id
        slug
        heroDesktopImageUrl
        heroMobileImageUrl
        startDate
        endDate
        winnerAnnouncedDate
        giveawayYoutubeId
        trailerYoutubeId
        rulesUrl
        projectName
        seoImageUrl
        entryVariantId
        bundlesCollectionId
        backgroundVideoUrl
        status
        title
        description
        squareImageUrl
        value {
          currencyAmount
          currencyCode
        }
      }
    }
  }
`

export const getSweepstake = async (
  client: ApolloClient<object>,
  projectSlug: string,
): Promise<SweepstakeObject | null> => {
  try {
    const { data } = await client.query<GetSweepstakeQuery, GetSweepstakeQueryVariables>({
      query: GET_SWEEPSTAKE,
      variables: { slug: projectSlug },
      errorPolicy: 'all',
    })

    return data?.getSweepstake?.sweepstake ?? null
  } catch (err) {
    logger().error('getSweepstake query failed', err)
    return null
  }
}

export const useSweepstake = (slug: string) => {
  const { data, ...rest } = useQuery<GetSweepstakeQuery, GetSweepstakeQueryVariables>(GET_SWEEPSTAKE, {
    variables: { slug },
    errorPolicy: 'all',
    skip: !slug,
  })

  return {
    sweepstake: data?.getSweepstake?.sweepstake,
    ...rest,
  }
}

export const GET_SWEEPSTAKES = gql`
  query getSweepstakes($input: GetSweepstakesInput!) {
    getSweepstakes(input: $input) {
      sweepstakes {
        id
        slug
        heroDesktopImageUrl
        heroMobileImageUrl
        startDate
        endDate
        winnerAnnouncedDate
        giveawayYoutubeId
        trailerYoutubeId
        rulesUrl
        projectName
        seoImageUrl
        entryVariantId
        bundlesCollectionId
        backgroundVideoUrl
        status
        title
        description
        squareImageUrl
        value {
          currencyAmount
          currencyCode
        }
      }
    }
  }
`

export const getSweepstakes = async (
  client: ApolloClient<object>,
  statuses: SweepstakeStatus[],
): Promise<SweepstakeObject[]> => {
  try {
    const { data } = await client.query<GetSweepstakesQuery, GetSweepstakesQueryVariables>({
      query: GET_SWEEPSTAKES,
      variables: { input: { statuses } },
      errorPolicy: 'all',
    })

    return compact(data?.getSweepstakes?.sweepstakes) ?? []
  } catch (err) {
    logger().error('getSweepstake query failed', err)
    return []
  }
}
